/**
 * Centralized Logger Utility
 * 
 * Provides a clean logging interface that can be disabled globally.
 * Logs are suppressed in production unless explicitly enabled via env var.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
}

class Logger {
  private config: LoggerConfig = {
    enabled: process.env.NODE_ENV === 'development',
    minLevel: 'debug'
  };

  private levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
  };

  /**
   * Enable or disable logging globally
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if logging is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Set minimum log level
   */
  setMinLevel(level: LogLevel): void {
    this.config.minLevel = level;
  }

  /**
   * Debug level log
   */
  debug(message: string, data?: any): void {
    this.log('debug', message, data);
  }

  /**
   * Info level log
   */
  info(message: string, data?: any): void {
    this.log('info', message, data);
  }

  /**
   * Warning level log
   */
  warn(message: string, data?: any): void {
    this.log('warn', message, data);
  }

  /**
   * Error level log
   */
  error(message: string, data?: any): void {
    this.log('error', message, data);
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, data?: any): void {
    if (!this.config.enabled) {
      return;
    }

    if (this.levels[level] < this.levels[this.config.minLevel]) {
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    if (data !== undefined) {
      console[level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log'](prefix, message, data);
    } else {
      console[level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log'](prefix, message);
    }
  }
}

// Create singleton instance
export const logger = new Logger();

// Disable logging in production by default
if (process.env.NODE_ENV === 'production') {
  logger.setEnabled(false);
}

export default logger;
